import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  useToast,
  useFranchise,
  useUpdateFranchise,
  useBusinesses,
} from "@finitless/shared/hooks";
import {
  apiClient,
  handleApiError,
  handleApiSuccess,
} from "@finitless/shared/lib";
import { PageHeader } from "@finitless/shared/components";
import { Icon, Breadcrumb } from "../../ui";
import { GeneralTab, BusinessesTab } from "../../ui/Brands";

const BrandDetailPage = () => {
  const { t } = useTranslation("brands");
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const franchiseId = parseInt(id);

  const { data: brand, isLoading, error, isError } = useFranchise(franchiseId);
  const updateMutation = useUpdateFranchise();

  const [formData, setFormData] = useState({
    name: "",
    internal_name: "",
    enabled: false,
    logo: null,
    country_phone_code: "",
    cellphone: "",
    phone: "",
    timezone: "",
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [logoInputKey, setLogoInputKey] = useState(0);
  const [phoneInputKey, setPhoneInputKey] = useState(0);

  const [activeTab, setActiveTab] = useState("general");

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBusinessIds, setSelectedBusinessIds] = useState(new Set());

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const { data: businessesData, isLoading: isLoadingBusinesses } =
    useBusinesses({
      enabledFilter: "all",
      searchTerm,
      page: currentPage,
      pageSize: 10,
    });

  const businesses = businessesData?.businesses || [];
  const pagination = businessesData?.pagination || {};

  const assignBusinessMutation = useMutation({
    mutationFn: async ({ businessId, franchiseId }) => {
      const response = await apiClient.post(`/business/${businessId}`, {
        franchise_id: franchiseId,
      });
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businesses"] });
    },
  });

  const assignBusinessBatchMutation = useMutation({
    mutationFn: async ({ businessId, franchiseId }) => {
      const response = await apiClient.post(`/business/${businessId}`, {
        franchise_id: franchiseId,
      });
      return response?.result;
    },
  });

  useEffect(() => {
    if (brand) {
      const normalizedCountryPhoneCode = String(
        brand.country_phone_code || "",
      ).replace(/\D/g, "");
      const normalizedCellphone = String(brand.cellphone || "")
        .replace(/\D/g, "")
        .slice(0, 15);
      const normalizedPhone = String(brand.phone || "")
        .replace(/\D/g, "")
        .slice(0, 15);

      const initialData = {
        name: brand.name || "",
        internal_name: brand.internal_name || "",
        enabled: brand.enabled || false,
        logo: null, // null means use original
        country_phone_code: normalizedCountryPhoneCode,
        cellphone: normalizedCellphone,
        phone: normalizedPhone,
        timezone: brand.timezone || "",
      };
      setFormData(initialData);
      setInitialFormData(initialData);
    }
  }, [brand]);

  useEffect(() => {
    if (activeTab === "businesses" && businesses.length > 0 && brand) {
      const assignedBusinessIds = businesses
        .filter((b) => b.franchise_id === franchiseId)
        .map((b) => b.id);
      setSelectedBusinessIds(new Set(assignedBusinessIds));
    }
  }, [activeTab, businesses, brand, franchiseId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        handleApiError(
          new Error(t("detailPage.toasts.invalidImageTypeMessage")),
          toast,
          {
            title: t("detailPage.toasts.invalidImageTypeTitle"),
            defaultMessage: t("detailPage.toasts.invalidImageTypeMessage"),
          },
        );
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        handleApiError(
          new Error(t("detailPage.toasts.imageTooLargeMessage")),
          toast,
          {
            title: t("detailPage.toasts.imageTooLargeTitle"),
            defaultMessage: t("detailPage.toasts.imageTooLargeMessage"),
          },
        );
        return;
      }

      try {
        // Convert file to base64
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64String = event.target.result;
          // Update logo in form data
          setFormData((prev) => ({
            ...prev,
            logo: { file, base64: base64String },
          }));
        };
        reader.onerror = () => {
          handleApiError(
            new Error(t("detailPage.toasts.readImageError")),
            toast,
            {
              title: t("common:states.error"),
              defaultMessage: t("detailPage.toasts.readImageError"),
            },
          );
        };
        reader.readAsDataURL(file);
      } catch (error) {
        handleApiError(error, toast, {
          title: t("detailPage.toasts.processImageErrorTitle"),
          defaultMessage: t("detailPage.toasts.processImageErrorMessage"),
        });
      }
    }
  };

  const handleSave = async () => {
    try {
      const payload = {};
      const normalizedCountryPhoneCode = String(
        formData.country_phone_code || "",
      ).replace(/\D/g, "");
      const normalizedCellphone = String(formData.cellphone || "")
        .replace(/\D/g, "")
        .slice(0, 15);
      const normalizedPhone = String(formData.phone || "")
        .replace(/\D/g, "")
        .slice(0, 15);

      if (formData.name !== initialFormData.name) {
        payload.name = formData.name;
      }
      if (formData.internal_name !== initialFormData.internal_name) {
        payload.internal_name = formData.internal_name;
      }
      if (formData.enabled !== initialFormData.enabled) {
        payload.enabled = formData.enabled;
      }
      if (formData.timezone !== initialFormData.timezone) {
        payload.timezone = formData.timezone;
      }

      const normalizedInitialCountryPhoneCode = String(
        initialFormData.country_phone_code || "",
      ).replace(/\D/g, "");
      const normalizedInitialCellphone = String(initialFormData.cellphone || "")
        .replace(/\D/g, "")
        .slice(0, 15);
      if (
        normalizedCountryPhoneCode !== normalizedInitialCountryPhoneCode ||
        normalizedCellphone !== normalizedInitialCellphone
      ) {
        payload.country_phone_code = normalizedCountryPhoneCode;
        payload.cellphone = normalizedCellphone;
      }

      const normalizedInitialPhone = String(initialFormData.phone || "")
        .replace(/\D/g, "")
        .slice(0, 15);
      if (normalizedPhone !== normalizedInitialPhone) {
        payload.phone = normalizedPhone;
      }

      // Handle logo changes
      if (formData.logo === "") {
        payload.logo = "";
      } else if (formData.logo && formData.logo !== null) {
        payload.logo = formData.logo.base64;
      }

      if (Object.keys(payload).length === 0) {
        handleApiSuccess(t("detailPage.toasts.noChanges"), toast, {
          title: t("detailPage.toasts.infoTitle"),
        });
        setIsEditing(false);
        return;
      }

      await updateMutation.mutateAsync({
        franchiseId,
        data: payload,
      });

      handleApiSuccess(t("detailPage.toasts.updateSuccess"), toast, {
        title: t("common:states.success"),
      });

      setInitialFormData(formData);
      setIsEditing(false);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("detailPage.toasts.updateErrorTitle"),
        defaultMessage: t("detailPage.toasts.updateErrorMessage"),
      });
    }
  };
  const handleRemoveLogo = () => {
    setFormData((prev) => ({ ...prev, logo: "" }));
    setLogoInputKey((prev) => prev + 1);
  };

  const handleCancel = () => {
    if (brand) {
      const normalizedCountryPhoneCode = String(
        brand.country_phone_code || "",
      ).replace(/\D/g, "");
      const normalizedCellphone = String(brand.cellphone || "")
        .replace(/\D/g, "")
        .slice(0, 15);
      const normalizedPhone = String(brand.phone || "")
        .replace(/\D/g, "")
        .slice(0, 15);

      const resetData = {
        name: brand.name || "",
        internal_name: brand.internal_name || "",
        enabled: brand.enabled || false,
        logo: null,
        timezone: brand.timezone || "",
        country_phone_code: normalizedCountryPhoneCode,
        cellphone: normalizedCellphone,
        phone: normalizedPhone,
      };
      setFormData(resetData);
      setInitialFormData(resetData);
      setPhoneInputKey((prev) => prev + 1);
    }
    setIsEditing(false);
  };

  const handleBackToBrands = () => {
    navigate("/menu/brands");
  };

  const hasChanges = () => {
    return (
      formData.name !== initialFormData.name ||
      formData.internal_name !== initialFormData.internal_name ||
      formData.enabled !== initialFormData.enabled ||
      formData.logo !== initialFormData.logo ||
      String(formData.country_phone_code || "") !==
        String(initialFormData.country_phone_code || "") ||
      String(formData.cellphone || "") !==
        String(initialFormData.cellphone || "") ||
      String(formData.phone || "") !== String(initialFormData.phone || "") ||
      formData.timezone !== initialFormData.timezone
    );
  };

  const handleBusinessToggle = async (businessId) => {
    const isSelected = selectedBusinessIds.has(businessId);

    try {
      await assignBusinessMutation.mutateAsync({
        businessId,
        franchiseId: isSelected ? null : franchiseId,
      });

      setSelectedBusinessIds((prev) => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.delete(businessId);
        } else {
          newSet.add(businessId);
        }
        return newSet;
      });

      toast.success(
        t("detailPage.toasts.businessAssignmentStatus", {
          action: isSelected
            ? t("detailPage.toasts.businessRemoved")
            : t("detailPage.toasts.businessAssigned"),
        }),
      );
    } catch (error) {
      console.error("Business assignment error:", error);
      toast.error(t("detailPage.toasts.businessAssignmentError"));
    }
  };

  const handleSelectAll = () => {
    const allBusinessIds = new Set(businesses.map((b) => b.id));

    const alreadyAllSelected =
      allBusinessIds.size > 0 &&
      Array.from(allBusinessIds).every((id) => selectedBusinessIds.has(id));

    if (alreadyAllSelected) {
      toast.info(t("detailPage.toasts.allBusinessesAlreadySelected"));
      return;
    }

    setSelectedBusinessIds(allBusinessIds);

    Promise.all(
      businesses
        .filter((b) => b.franchise_id !== franchiseId)
        .map((business) =>
          assignBusinessBatchMutation.mutateAsync({
            businessId: business.id,
            franchiseId: franchiseId,
          }),
        ),
    )
      .then(() => {
        // Invalidate only once after all operations complete
        queryClient.invalidateQueries({ queryKey: ["businesses"] });
        toast.success(t("detailPage.toasts.allBusinessesAssigned"));
      })
      .catch(() => {
        toast.error(t("detailPage.toasts.assignBusinessesError"));
      });
  };

  const handleSelectNone = () => {
    const alreadyNoneSelected = selectedBusinessIds.size === 0;

    if (alreadyNoneSelected) {
      toast.info(t("detailPage.toasts.noBusinessesSelected"));
      return;
    }

    const idsToUnselect = Array.from(selectedBusinessIds);
    setSelectedBusinessIds(new Set());

    Promise.all(
      businesses
        .filter((b) => idsToUnselect.includes(b.id))
        .map((business) =>
          assignBusinessBatchMutation.mutateAsync({
            businessId: business.id,
            franchiseId: null,
          }),
        ),
    )
      .then(() => {
        // Invalidate only once after all operations complete
        queryClient.invalidateQueries({ queryKey: ["businesses"] });
        toast.success(t("detailPage.toasts.allBusinessesUnassigned"));
      })
      .catch(() => {
        toast.error(t("detailPage.toasts.unassignBusinessesError"));
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClearSearch = () => {
    setSearchInput("");
  };

  // Callback to update businesses when selecting all
  const handleUpdateBusinesses = async (businessArray) => {
    // Get all businesses that should be assigned
    const businessesToAssign = businessArray.filter(
      (id) => !selectedBusinessIds.has(id),
    );
    // Get all businesses that should be unassigned
    const businessesToUnassign = Array.from(selectedBusinessIds).filter(
      (id) => !businessArray.includes(id),
    );

    try {
      // Assign new businesses
      await Promise.all(
        businessesToAssign.map((businessId) =>
          assignBusinessBatchMutation.mutateAsync({
            businessId,
            franchiseId: franchiseId,
          }),
        ),
      );

      // Unassign businesses that are no longer selected
      await Promise.all(
        businessesToUnassign.map((businessId) =>
          assignBusinessBatchMutation.mutateAsync({
            businessId,
            franchiseId: null,
          }),
        ),
      );

      // Invalidate queries after all operations complete
      queryClient.invalidateQueries({ queryKey: ["businesses"] });
      toast.success(t("detailPage.toasts.businessesUpdated"));
    } catch (error) {
      console.error("Error updating businesses:", error);
      toast.error(t("detailPage.toasts.updateBusinessesError"));
      throw error; // Re-throw to let BusinessesTab handle the reversion
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500"></div>
          <p className="text-gray-400">{t("detailPage.states.loading")}</p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !brand)) {
    return (
      <div className="py-12 text-center">
        <Icon
          name="Building"
          className="mx-auto mb-4 h-12 w-12 text-gray-400"
        />
        <h3 className="mb-2 text-sm font-medium text-gray-300">
          {t("detailPage.states.notFoundTitle")}
        </h3>
        <p className="text-gray-400">
          {error?.message || t("detailPage.states.notFoundDescription")}
        </p>
        <button
          onClick={handleBackToBrands}
          className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          {t("detailPage.actions.backToBrands")}
        </button>
      </div>
    );
  }

  const breadcrumbItems = [
    {
      label: t("detailPage.breadcrumb.brands"),
      path: "/menu/brands",
      icon: "Building",
    },
    {
      label: brand
        ? `${brand.name.slice(0, 20)}${brand.name.length > 20 ? "..." : ""}`
        : t("detailPage.states.loadingShort"),
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
          title={brand.name}
          subtitle={t("detailPage.header.subtitle")}
        />
        <div className="flex space-x-3">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              disabled={activeTab !== "general"}
              className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-600"
            >
              <Icon name="Edit" className="h-4 w-4" />
              <span>{t("common:actions.edit")}</span>
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 rounded-lg bg-gray-800 px-4 py-2 text-white transition-colors hover:bg-gray-700"
              >
                <Icon name="X" className="h-4 w-4" />
                <span>{t("common:actions.cancel")}</span>
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending || !hasChanges()}
                className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-600"
              >
                {updateMutation.isPending ? (
                  <Icon name="Loader" className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Icon name="Check" className="h-4 w-4" />
                    <span>{t("common:actions.save")}</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="rounded-lg border border-gray-800 bg-gray-900">
        <div className="border-b border-gray-800">
          <nav className="flex space-x-4 overflow-x-auto px-6 sm:space-x-8">
            <button
              onClick={() => setActiveTab("general")}
              className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                activeTab === "general"
                  ? "border-blue-500 text-white"
                  : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
              }`}
            >
              {t("detailPage.tabs.general")}
            </button>
            <button
              onClick={() => setActiveTab("businesses")}
              className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                activeTab === "businesses"
                  ? "border-blue-500 text-white"
                  : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
              }`}
            >
              {t("detailPage.tabs.businesses")}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* General Tab */}
          {activeTab === "general" && (
            <GeneralTab
              formData={formData}
              handleInputChange={handleInputChange}
              isEditing={isEditing}
              updateMutation={updateMutation}
              handleLogoChange={handleLogoChange}
              logoInputKey={logoInputKey}
              phoneInputKey={phoneInputKey}
              brand={brand}
              handleRemoveLogo={handleRemoveLogo}
            />
          )}

          {/* Businesses Tab */}
          {activeTab === "businesses" && (
            <BusinessesTab
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              handleClearSearch={handleClearSearch}
              handleSelectAll={handleSelectAll}
              handleSelectNone={handleSelectNone}
              handleBusinessToggle={handleBusinessToggle}
              selectedBusinessIds={selectedBusinessIds}
              setSelectedBusinessIds={setSelectedBusinessIds}
              isLoadingBusinesses={isLoadingBusinesses}
              assignBusinessMutation={assignBusinessMutation}
              businesses={businesses}
              currentPage={currentPage}
              pagination={pagination}
              handlePageChange={handlePageChange}
              searchTerm={searchTerm}
              onUpdateBusinesses={handleUpdateBusinesses}
              initialSelectedBusinessIds={Array.from(selectedBusinessIds)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandDetailPage;
